import { useTranslation } from "react-i18next";

/**
 * Per-group pricing (typePrice 3) charges a flat rate per *thing*, not per
 * person — one quad, one buggy, one jet ski, one boat. Calling that thing a
 * "group" reads as "the price covers our whole party", which is how travellers
 * have been misreading it on WhatsApp ("700 per group means 700 for all of us,
 * right?"). Naming the actual unit removes the ambiguity: "1 quad · 700 MAD
 * per quad" can only be read one way.
 *
 * Categories with no obvious physical unit (private tours, excursions) keep the
 * generic "group" wording.
 */
import { resolveBookingUnit, type BookingUnit } from "@majourneys/shared";

export { resolveBookingUnit, type BookingUnit };

interface UnitKeys {
  /** Section header above the stepper — "Quads". */
  title: string;
  /** Capitalised singular used as a summary-row label — "Quad". */
  singular: string;
  /** Lowercase noun following a count — "quad" / "quads". */
  one: string;
  other: string;
  /** Rate suffix — "per quad". */
  per: string;
  /** Stepper button aria-labels. */
  add: string;
  remove: string;
}

/**
 * "group" keeps the legacy `activity.*` keys so the seven existing locale
 * translations stay in force; the machine units live under `booking_unit.*`.
 */
const KEYS: Record<BookingUnit, UnitKeys> = {
  group: {
    title: "activity.groups",
    singular: "activity.group_singular",
    one: "activity.group_singular",
    other: "activity.groups_plural",
    per: "activity.per_group",
    add: "activity.add_group",
    remove: "activity.remove_group",
  },
  quad: unitKeys("quad"),
  twizy: unitKeys("twizy"),
  car: unitKeys("car"),
  buggy: unitKeys("buggy"),
  jet_ski: unitKeys("jet_ski"),
  boat: unitKeys("boat"),
  table: unitKeys("table"),
};

function unitKeys(unit: Exclude<BookingUnit, "group">): UnitKeys {
  return {
    title: `booking_unit.${unit}.title`,
    singular: `booking_unit.${unit}.singular`,
    one: `booking_unit.${unit}.one`,
    other: `booking_unit.${unit}.other`,
    per: `booking_unit.${unit}.per`,
    add: `booking_unit.${unit}.add`,
    remove: `booking_unit.${unit}.remove`,
  };
}

/**
 * English fallbacks so a unit renders correctly even before a locale file
 * carries the key (i18next falls back silently, which would otherwise put
 * "booking_unit.quad.per" on screen).
 */
const FALLBACKS: Record<BookingUnit, UnitKeys> = {
  group: {
    title: "Groups",
    singular: "Group",
    one: "group",
    other: "groups",
    per: "per group",
    add: "Add group",
    remove: "Remove group",
  },
  car: {
    title: "Cars",
    singular: "Car",
    one: "car",
    other: "cars",
    per: "per car",
    add: "Add car",
    remove: "Remove car",
  },
  twizy: {
    title: "Twizys",
    singular: "Twizy",
    one: "Twizy",
    other: "Twizys",
    per: "per Twizy",
    add: "Add Twizy",
    remove: "Remove Twizy",
  },
  quad: {
    title: "Quads",
    singular: "Quad",
    one: "quad",
    other: "quads",
    per: "per quad",
    add: "Add quad",
    remove: "Remove quad",
  },
  buggy: {
    title: "Buggies",
    singular: "Buggy",
    one: "buggy",
    other: "buggies",
    per: "per buggy",
    add: "Add buggy",
    remove: "Remove buggy",
  },
  jet_ski: {
    title: "Jet skis",
    singular: "Jet ski",
    one: "jet ski",
    other: "jet skis",
    per: "per jet ski",
    add: "Add jet ski",
    remove: "Remove jet ski",
  },
  boat: {
    title: "Boats",
    singular: "Boat",
    one: "boat",
    other: "boats",
    per: "per boat",
    add: "Add boat",
    remove: "Remove boat",
  },
  table: {
    title: "Tables",
    singular: "Table",
    one: "table",
    other: "tables",
    per: "per table",
    add: "Add table",
    remove: "Remove table",
  },
};

export interface BookingUnitLabels {
  unit: BookingUnit;
  /** Section header — "Quads". */
  title: string;
  /** Capitalised singular for summary rows — "Quad". */
  singular: string;
  /** Capitalised plural for summary rows — "Quads". */
  plural: string;
  /** Lowercase noun to follow a count — count(2) === "quads". */
  count: (n: number) => string;
  /** Rate suffix — "per quad". */
  per: string;
  add: string;
  remove: string;
}

/**
 * The "per quad" rate suffix for a flat-rate price, outside React: its key and
 * the English fallback, for a caller that translates with its own `t` (the
 * listing cards). Same answer as `useBookingUnitLabels(...).per`, so a card
 * and the page it opens can never name a different unit.
 */
export function bookingUnitPerKey(
  categorySlug?: string | null,
  activitySlug?: string | null,
): { key: string; fallback: string } {
  const unit = resolveBookingUnit(categorySlug, activitySlug);
  return { key: KEYS[unit].per, fallback: FALLBACKS[unit].per };
}

export function useBookingUnitLabels(
  categorySlug?: string | null,
  activitySlug?: string | null,
): BookingUnitLabels {
  const { t } = useTranslation();
  const unit = resolveBookingUnit(categorySlug, activitySlug);
  const keys = KEYS[unit];
  const fallback = FALLBACKS[unit];
  const tr = (field: keyof UnitKeys) => t(keys[field], { defaultValue: fallback[field] });

  const one = tr("one");
  const other = tr("other");

  return {
    unit,
    title: tr("title"),
    singular: tr("singular"),
    plural: tr("title"),
    // Lowercased so it reads naturally after a number ("2 quads"). Arabic and
    // other non-cased scripts are unaffected by toLowerCase().
    // Lowercased for common nouns; a brand name like Twizy keeps its capital.
    count: (n: number) => {
      const noun = n === 1 ? one : other;
      return unit === "twizy" ? noun : noun.toLowerCase();
    },
    per: tr("per"),
    add: tr("add"),
    remove: tr("remove"),
  };
}
